import { useState, useEffect } from "react";
import { ZoomIn } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { fetchAnnualWheel } from "../../services/businessDashApi";

const AnnualWheel = ({ year = new Date().getFullYear() }) => {
  const [showDetailPopup, setShowDetailPopup] = useState(false);
  const [sortColumn, setSortColumn] = useState("date");
  const [sortDirection, setSortDirection] = useState("asc");

  // API Integration
  const [eventsData, setEventsData] = useState({});
  const [isLoadingEvents, setIsLoadingEvents] = useState(true);

  useEffect(() => {
    const loadAnnualData = async () => {
      try {
        setIsLoadingEvents(true);
        const data = await fetchAnnualWheel(year);
        // data.months is already in correct format: { "1": [...], "2": [...], ... "12": [...] }
        setEventsData(data.months || {});
      } catch (error) {
        console.error("Failed to load annual wheel data:", error);
      } finally {
        setIsLoadingEvents(false);
      }
    };

    loadAnnualData();
  }, [year]);

  const [ringSegments] = useState([
    // Ring 1 (innermost) - Board Meetings (Yellow) - now with quarters
    {
      id: "q1",
      startAngle: 0,
      endAngle: 90,
      label: "Q1",
      type: "events",
      dates: "Jan-Mar",
      ring: 1,
    },
    {
      id: "q2",
      startAngle: 90,
      endAngle: 180,
      label: "Q2",
      type: "events",
      dates: "Apr-Jun",
      ring: 1,
    },
    {
      id: "q3",
      startAngle: 180,
      endAngle: 270,
      label: "Q3",
      type: "events",
      dates: "Jul-Sep",
      ring: 1,
    },
    {
      id: "q4",
      startAngle: 270,
      endAngle: 360,
      label: "Q4",
      type: "events",
      dates: "Oct-Dec",
      ring: 1,
    },

    // Ring 2 - Company Events (Pink) - no text
    {
      id: "a1",
      startAngle: 0,
      endAngle: 60,
      label: "",
      type: "assignments",
      dates: "Jan-Feb",
      ring: 2,
    },
    {
      id: "a2",
      startAngle: 60,
      endAngle: 120,
      label: "",
      type: "assignments",
      dates: "Mar-Apr",
      ring: 2,
    },
    {
      id: "a3",
      startAngle: 120,
      endAngle: 180,
      label: "",
      type: "assignments",
      dates: "May-Jun",
      ring: 2,
    },
    {
      id: "a4",
      startAngle: 180,
      endAngle: 240,
      label: "",
      type: "assignments",
      dates: "Jul-Aug",
      ring: 2,
    },
    {
      id: "a5",
      startAngle: 240,
      endAngle: 300,
      label: "",
      type: "assignments",
      dates: "Sep-Oct",
      ring: 2,
    },
    {
      id: "a6",
      startAngle: 300,
      endAngle: 360,
      label: "",
      type: "assignments",
      dates: "Nov-Dec",
      ring: 2,
    },

    // Ring 3 (outermost) - Months (Blue)
    {
      id: "jan",
      startAngle: 0,
      endAngle: 30,
      label: "Jan",
      type: "restudy",
      dates: "January",
      ring: 3,
    },
    {
      id: "feb",
      startAngle: 30,
      endAngle: 60,
      label: "Feb",
      type: "restudy",
      dates: "February",
      ring: 3,
    },
    {
      id: "mar",
      startAngle: 60,
      endAngle: 90,
      label: "Mar",
      type: "restudy",
      dates: "March",
      ring: 3,
    },
    {
      id: "apr",
      startAngle: 90,
      endAngle: 120,
      label: "Apr",
      type: "restudy",
      dates: "April",
      ring: 3,
    },
    {
      id: "may",
      startAngle: 120,
      endAngle: 150,
      label: "May",
      type: "restudy",
      dates: "May",
      ring: 3,
    },
    {
      id: "jun",
      startAngle: 150,
      endAngle: 180,
      label: "Jun",
      type: "restudy",
      dates: "June",
      ring: 3,
    },
    {
      id: "jul",
      startAngle: 180,
      endAngle: 210,
      label: "Jul",
      type: "restudy",
      dates: "July",
      ring: 3,
    },
    {
      id: "aug",
      startAngle: 210,
      endAngle: 240,
      label: "Aug",
      type: "restudy",
      dates: "August",
      ring: 3,
    },
    {
      id: "sep",
      startAngle: 240,
      endAngle: 270,
      label: "Sep",
      type: "restudy",
      dates: "September",
      ring: 3,
    },
    {
      id: "oct",
      startAngle: 270,
      endAngle: 300,
      label: "Oct",
      type: "restudy",
      dates: "October",
      ring: 3,
    },
    {
      id: "nov",
      startAngle: 300,
      endAngle: 330,
      label: "Nov",
      type: "restudy",
      dates: "November",
      ring: 3,
    },
    {
      id: "dec",
      startAngle: 330,
      endAngle: 360,
      label: "Dec",
      type: "restudy",
      dates: "December",
      ring: 3,
    },
  ]);

  const meetingDetails = [
    {
      id: 1,
      date: "2025-03-15",
      name: "Q1 Board Meeting",
      type: "Board Meeting",
      quarter: 1,
      month: 3,
      attendees: [
        "Sarah Johnson",
        "Michael Brown",
        "Emily Davis",
        "Robert Wilson",
        "Lisa Anderson",
        "James Miller",
        "Anna Taylor",
        "David Lee",
      ],
      documents: ["Q1 Financial Report", "Strategic Review", "Board Minutes"],
    },
    {
      id: 2,
      date: "2025-04-20",
      name: "Annual General Meeting",
      type: "Company Event",
      quarter: 2,
      month: 4,
      attendees: ["All Board Members", "Executive Team", "Key Stakeholders"],
      documents: ["AGM Presentation", "Annual Report", "Voting Records"],
    },
    {
      id: 3,
      date: "2025-06-01",
      name: "ESG Reporting",
      type: "Sustainability",
      quarter: 2,
      month: 6,
      attendees: ["ESG Committee", "External Auditor"],
      documents: ["ESG Report Draft", "Sustainability Metrics"],
    },
    {
      id: 4,
      date: "2025-06-15",
      name: "Q2 Board Meeting",
      type: "Board Meeting",
      quarter: 2,
      month: 6,
      attendees: [
        "Sarah Johnson",
        "Michael Brown",
        "Emily Davis",
        "Robert Wilson",
        "Lisa Anderson",
        "James Miller",
        "Anna Taylor",
        "David Lee",
        "Thomas Clark",
      ],
      documents: ["Q2 Review", "Strategy Update", "Financial Statements"],
    },
    {
      id: 5,
      date: "2025-07-30",
      name: "Summer Strategy Review",
      type: "Company Event",
      quarter: 3,
      month: 7,
      attendees: ["Executive Team", "Strategy Committee"],
      documents: ["Strategy Framework", "Market Analysis"],
    },
    {
      id: 6,
      date: "2025-08-15",
      name: "Sustainability Workshop",
      type: "Sustainability",
      quarter: 3,
      month: 8,
      attendees: ["All Board Members", "ESG Consultant"],
      documents: ["Workshop Materials", "Action Plan"],
    },
    {
      id: 7,
      date: "2025-09-15",
      name: "Q3 Board Meeting",
      type: "Board Meeting",
      quarter: 3,
      month: 9,
      attendees: ["All Board Members"],
      documents: ["Q3 Financial Report", "Performance Review"],
    },
    {
      id: 8,
      date: "2025-10-10",
      name: "Sustainability Audit",
      type: "Sustainability",
      quarter: 4,
      month: 10,
      attendees: ["ESG Committee", "External Auditor"],
      documents: ["Audit Framework", "Compliance Review"],
    },
    {
      id: 9,
      date: "2025-12-15",
      name: "Q4 Board Meeting",
      type: "Board Meeting",
      quarter: 4,
      month: 12,
      attendees: ["All Board Members"],
      documents: ["Annual Review", "Budget Planning"],
    },
  ];

  const getMeetingsForSegment = (segment) => {
    if (segment.ring === 1) {
      // Quarterly segments (yellow ring - board meetings)
      const quarter = parseInt(segment.id.replace("q", ""));
      return meetingDetails.filter(
        (meeting) =>
          meeting.quarter === quarter && meeting.type === "Board Meeting"
      );
    } else if (segment.ring === 2) {
      // Bi-monthly segments (pink ring - sustainability events)
      const monthRanges = {
        a1: [1, 2],
        a2: [3, 4],
        a3: [5, 6],
        a4: [7, 8],
        a5: [9, 10],
        a6: [11, 12],
      };
      const months = monthRanges[segment.id] || [];
      return meetingDetails.filter(
        (meeting) =>
          months.includes(meeting.month) && meeting.type === "Sustainability"
      );
    } else if (segment.ring === 3) {
      // Monthly segments (blue ring - all meetings in that month)
      const monthMap = {
        jan: 1,
        feb: 2,
        mar: 3,
        apr: 4,
        may: 5,
        jun: 6,
        jul: 7,
        aug: 8,
        sep: 9,
        oct: 10,
        nov: 11,
        dec: 12,
      };
      const month = monthMap[segment.id];
      return meetingDetails.filter((meeting) => meeting.month === month);
    }
    return [];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedEvents = [...meetingDetails].sort((a, b) => {
    let aValue, bValue;

    if (sortColumn === "date") {
      aValue = new Date(a.date).getTime();
      bValue = new Date(b.date).getTime();
    } else {
      aValue = a.type.toLowerCase();
      bValue = b.type.toLowerCase();
    }

    if (sortDirection === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const getSegmentColor = (type) => {
    switch (type) {
      case "subjects":
        return "hsl(var(--pastel-blue))";
      case "events":
        return "hsl(var(--pastel-yellow))";
      case "assignments":
        return "hsl(var(--pastel-pink))";
      case "restudy":
        return "hsl(var(--pastel-blue))";
      default:
        return "hsl(var(--muted))";
    }
  };

  const getRingRadius = (ring) => {
    const baseRadius = 40;
    const ringWidth = 30;
    return baseRadius + (ring - 1) * ringWidth;
  };

  const getRingWidth = () => 25;

  const createSegmentPath = (segment) => {
    const centerX = 160;
    const centerY = 160;
    const innerRadius = getRingRadius(segment.ring);
    const outerRadius = innerRadius + getRingWidth();

    const startAngle = (segment.startAngle - 90) * (Math.PI / 180);
    const endAngle = (segment.endAngle - 90) * (Math.PI / 180);

    const x1 = centerX + innerRadius * Math.cos(startAngle);
    const y1 = centerY + innerRadius * Math.sin(startAngle);
    const x2 = centerX + outerRadius * Math.cos(startAngle);
    const y2 = centerY + outerRadius * Math.sin(startAngle);

    const x3 = centerX + outerRadius * Math.cos(endAngle);
    const y3 = centerY + outerRadius * Math.sin(endAngle);
    const x4 = centerX + innerRadius * Math.cos(endAngle);
    const y4 = centerY + innerRadius * Math.sin(endAngle);

    const largeArc = segment.endAngle - segment.startAngle > 180 ? 1 : 0;

    return `M ${x1} ${y1} L ${x2} ${y2} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x3} ${y3} L ${x4} ${y4} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x1} ${y1}`;
  };

  const getTextPosition = (segment) => {
    const centerX = 160;
    const centerY = 160;
    const radius = getRingRadius(segment.ring) + getRingWidth() / 2;
    const midAngle =
      ((segment.startAngle + segment.endAngle) / 2 - 90) * (Math.PI / 180);

    const x = centerX + radius * Math.cos(midAngle);
    const y = centerY + radius * Math.sin(midAngle);

    return { x, y };
  };

  const WheelComponent = ({ size }) => {
    const wheelSize = size === "large" ? 600 : 320;
    const scale = size === "large" ? 1.875 : 1;

    return (
      <div className="relative" style={{ width: wheelSize, height: wheelSize }}>
        <svg
          width={wheelSize}
          height={wheelSize}
          className="overflow-visible cursor-pointer"
          onClick={() => setShowDetailPopup(true)}
        >
          {ringSegments.map((segment) => {
            const segmentMeetings = getMeetingsForSegment(segment);

            if (segmentMeetings.length === 0) {
              return (
                <g key={segment.id} transform={`scale(${scale})`}>
                  <path
                    d={createSegmentPath(segment)}
                    fill={getSegmentColor(segment.type)}
                    stroke="white"
                    strokeWidth="1"
                    className="hover:opacity-80 transition-opacity"
                  />
                  {segment.label && (
                    <text
                      x={getTextPosition(segment).x}
                      y={getTextPosition(segment).y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="text-xs font-medium fill-foreground pointer-events-none"
                      style={{ fontSize: size === "large" ? "10px" : "8px" }}
                    >
                      {segment.label}
                    </text>
                  )}
                </g>
              );
            }

            return (
              <HoverCard key={segment.id}>
                <HoverCardTrigger asChild>
                  <g transform={`scale(${scale})`} className="cursor-pointer">
                    <path
                      d={createSegmentPath(segment)}
                      fill={getSegmentColor(segment.type)}
                      stroke="white"
                      strokeWidth="1"
                      className="hover:opacity-80 transition-opacity"
                    />
                    {segment.label && (
                      <text
                        x={getTextPosition(segment).x}
                        y={getTextPosition(segment).y}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="text-xs font-medium fill-foreground pointer-events-none"
                        style={{ fontSize: size === "large" ? "10px" : "8px" }}
                      >
                        {segment.label}
                      </text>
                    )}
                  </g>
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm">
                      {segment.ring === 1
                        ? `${segment.label} Meetings`
                        : segment.ring === 2
                        ? `${segment.dates} Events`
                        : `${segment.dates} Meetings`}
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-1 px-2">Date</th>
                            <th className="text-left py-1 px-2">Meeting</th>
                            <th className="text-left py-1 px-2">Attendees</th>
                          </tr>
                        </thead>
                        <tbody>
                          {segmentMeetings.map((meeting) => (
                            <tr
                              key={meeting.id}
                              className="border-b border-border/30"
                            >
                              <td className="py-1 px-2 font-medium">
                                {formatDate(meeting.date)}
                              </td>
                              <td className="py-1 px-2">{meeting.name}</td>
                              <td className="py-1 px-2 text-xs">
                                {meeting.attendees.length > 3
                                  ? `${meeting.attendees
                                      .slice(0, 2)
                                      .join(", ")} +${
                                      meeting.attendees.length - 2
                                    } more`
                                  : meeting.attendees.join(", ")}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {segmentMeetings.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        <strong>Documents:</strong>{" "}
                        {segmentMeetings[0].documents.join(", ")}
                      </div>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
            );
          })}

          {/* Center circle with year */}
          <circle
            cx={160 * scale}
            cy={160 * scale}
            r={35 * scale}
            fill="hsl(var(--background))"
            stroke="hsl(var(--border))"
            strokeWidth="2"
          />
          <text
            x={160 * scale}
            y={160 * scale}
            textAnchor="middle"
            dominantBaseline="middle"
            className="text-2xl font-bold fill-foreground"
            style={{ fontSize: size === "large" ? "24px" : "16px" }}
          >
            {year}
          </text>
        </svg>

        {size === "small" && (
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2 p-1 h-8 w-8"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <div className="flex justify-center">
                <WheelComponent size="large" />
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col items-center space-y-6">
        <h4 className="text-lg font-medium text-foreground">
          Annual Board Wheel
        </h4>

        <WheelComponent size="small" />

        {/* Data Key/Legend */}
        <div className="grid grid-cols-2 gap-6 text-sm">
          <div className="space-y-3">
            <h5 className="font-semibold text-foreground">Ring Categories</h5>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded border"
                  style={{ backgroundColor: "hsl(var(--pastel-blue))" }}
                ></div>
                <span className="text-foreground/80">Months (Outer Ring)</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded border"
                  style={{ backgroundColor: "hsl(var(--pastel-pink))" }}
                ></div>
                <span className="text-foreground/80">Events (Middle Ring)</span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded border"
                  style={{ backgroundColor: "hsl(var(--pastel-yellow))" }}
                ></div>
                <span className="text-foreground/80">
                  Quarters (Inner Ring)
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h5 className="font-semibold text-foreground">Usage</h5>
            <div className="text-xs text-foreground/70 space-y-1">
              <p>• Click the wheel to view annual events</p>
              <p>• Each ring represents different time divisions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Annual Events Detail Popup */}
      <Dialog open={showDetailPopup} onOpenChange={setShowDetailPopup}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Annual Board Events {year}</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border text-sm">
              <thead>
                <tr className="bg-muted">
                  <th
                    className="border border-border p-2 text-left cursor-pointer hover:bg-muted/70"
                    onClick={() => handleSort("date")}
                  >
                    Date{" "}
                    {sortColumn === "date" &&
                      (sortDirection === "asc" ? "▲" : "▼")}
                  </th>
                  <th className="border border-border p-2 text-left">Event</th>
                  <th
                    className="border border-border p-2 text-left cursor-pointer hover:bg-muted/70"
                    onClick={() => handleSort("type")}
                  >
                    Type{" "}
                    {sortColumn === "type" &&
                      (sortDirection === "asc" ? "▲" : "▼")}
                  </th>
                  <th className="border border-border p-2 text-left">Status</th>
                  <th className="border border-border p-2 text-left">
                    Attendance
                  </th>
                  <th className="border border-border p-2 text-left">
                    Documents
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((event) => (
                  <tr key={event.id} className="hover:bg-muted/50">
                    <td className="border border-border p-2 font-medium">
                      {new Date(event.date).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </td>
                    <td className="border border-border p-2">{event.name}</td>
                    <td className="border border-border p-2">{event.type}</td>
                    <td className="border border-border p-2">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          new Date(event.date) < new Date()
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                        }`}
                      >
                        {new Date(event.date) < new Date()
                          ? "Completed"
                          : "Upcoming"}
                      </span>
                    </td>
                    <td className="border border-border p-2">
                      {event.attendees.length} attendees
                    </td>
                    <td className="border border-border p-2 text-xs">
                      {event.documents.join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AnnualWheel;
